import { useCallback, useMemo, useRef, useState } from 'react'
import { useService } from '../../../hooks/use-service'
import { NotificationService } from '../../../services'
import {
    CompositeImage,
    INotificationFieldSuggestions,
    IPageAnalysisError,
} from '../../../interfaces/notification-field-suggestions'
import { DomainDto } from '../../../dtos/domain'
import { GenerateNotificationVariantsRequest } from '@pushly/models/lib/structs'
import { sanitizeKeywords } from '../../../_utils/keywords'
import { useCompositeImageLoader } from './use-composite-image-loader'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import { pause } from '@pushly/aqe/lib/utils'

/**
 * Result from a Pushly AI generation request.
 * Contains suggestions (single or all variants) or an error, never both.
 */
export type PushlyAiResult = {
    suggestions: INotificationFieldSuggestions | null
    allSuggestions: INotificationFieldSuggestions[] | null
    error: IPageAnalysisError | null
}

/**
 * API surface for the Pushly AI hook.
 * Provides generation functions that return structured results with error handling.
 */
export type PushlyAiApi = {
    readonly isGenerating: boolean
    readonly generationIdRef: React.RefObject<string | null>
    readonly lastServedVariantIndexRef: React.RefObject<number | null>
    generateNotificationVariant(req: NotificationVariantModel): Promise<PushlyAiResult>
    generateNotificationVariants(req: NotificationVariantModel): Promise<PushlyAiResult>
    /** Mark specific variant indices as used so auto-fill skips them */
    markVariantsUsed(indices: number[]): void
}

type PageAnalysisDomain = Pick<DomainDto, 'id' | 'mediaQuality'>

const buildResponse = (data: INotificationFieldSuggestions | null): INotificationFieldSuggestions | null => {
    if (!data || Object.values(data).every((v) => (Array.isArray(v) ? !v.length : v === null))) return null
    return data
}

const buildArrayResponse = (data: INotificationFieldSuggestions[] | null): INotificationFieldSuggestions[] | null => {
    if (!data || data.length === 0) return null
    return data.filter((variant) => !Object.values(variant).every((v) => (Array.isArray(v) ? !v.length : v === null)))
}

const buildGenerationRequest = (domainId: number, source: NotificationVariantModel) => {
    const audience = source.getAudience()
    const content = source.getContent()

    return GenerateNotificationVariantsRequest.from({
        // The current iteration of Pushly AI is restricted to the legacy flow.
        flow: 'legacy',
        domain_id: domainId,
        // The current iteration of Pushly AI is restricted to a single segment ID.
        segment_id: audience.getSegmentIds()?.[0]!,
        // Request all four variations at once and then cache them.
        num_variations: 4,
        // Default fewshot for segment history to seven days.
        fewshot_max_days: 7,
        // v1 Pushly AI in pufferfish does not support generating variants
        // with custom content. ONLY the landing URL is sent for generation
        // requests.
        content: {
            landing_url: content.getDefaultContent().getLandingUrl()!,
            // title: sourceContent.getDefaultContent().getTitle() ?? undefined,
            // body: sourceContent.getDefaultContent().getBody() ?? undefined,
            // keywords: sourceContent.getDefaultContent().getKeywords() ?? undefined,
        },
        // The current iteration only allows a single generation request per domain.
        skip_cache: false,
        // Server-authored (Brand Voice): sailfish resolves and overwrites these —
        // never sent by the client, but GenerateNotificationVariantsRequest.from() requires
        // them explicitly (they default to [] on the struct, but that default doesn't make
        // the field optional in .from()'s own type).
        assertion_checks: [],
        prior_violations: [],
    })
}

export const usePushlyAi = ({ id: domainId, mediaQuality = 40 }: PageAnalysisDomain): PushlyAiApi => {
    if (!domainId) throw new Error('usePushlyAi: Domain ID could not be determined')

    const notifSvc = useService(NotificationService)

    const [isGenerating, setIsGenerating] = useState(false)
    const isGeneratingRef = useRef(false)

    // Helper to keep ref and state in sync. The ref is the source of truth
    // inside the async callback (avoids stale closure), while the state
    // drives re-renders for consumers that read `isGenerating`.
    const setGenerating = useCallback((value: boolean) => {
        isGeneratingRef.current = value
        setIsGenerating(value)
    }, [])

    const lastReqRef = useRef<GenerateNotificationVariantsRequest | null>(null)
    const resultsCacheRef = useRef<INotificationFieldSuggestions[]>([])
    // Stores the request_id from the most recent API response.
    // Persisted across cache hits so it can be used when a user accepts a variant.
    const generationIdRef = useRef<string | null>(null)
    // Tracks the 0-based index of the last variant served in single mode.
    // Used by the auto-fill path to include variant_index in sourceMetadata.
    const lastServedVariantIndexRef = useRef<number | null>(null)
    // Tracks which variant indices have been consumed by either auto-fill (single)
    // or the advanced review drawer (all). When all indices are used the set is
    // cleared so the cycle restarts from index 0.
    const usedIndicesRef = useRef<Set<number>>(new Set())

    const loadCompositeImage = useCompositeImageLoader({ id: domainId, mediaQuality })

    const generateVariants = useCallback(
        async <T extends 'single' | 'all'>(source: NotificationVariantModel, mode: T): Promise<PushlyAiResult> => {
            if (isGeneratingRef.current) {
                return { suggestions: buildResponse(null), allSuggestions: buildArrayResponse(null), error: null }
            }

            try {
                setGenerating(true)

                const req = buildGenerationRequest(domainId, source)

                // Check if we can use cached results with circular array pattern (single mode only)
                if (mode === 'single' && lastReqRef.current?.equals(req) && resultsCacheRef.current.length) {
                    await pause(500)

                    const resLength = resultsCacheRef.current.length

                    // If all indices have been used, clear the set to restart the cycle
                    if (usedIndicesRef.current.size >= resLength) {
                        usedIndicesRef.current.clear()
                    }

                    // Find the next unused index starting from 0
                    let nextIndex = 0
                    while (usedIndicesRef.current.has(nextIndex) && nextIndex < resLength) {
                        nextIndex++
                    }

                    usedIndicesRef.current.add(nextIndex)

                    lastServedVariantIndexRef.current = nextIndex

                    return {
                        suggestions: buildResponse(resultsCacheRef.current[nextIndex]),
                        allSuggestions: null,
                        error: null,
                    }
                }

                // If the req matches the cached one and we have results, return cached results (all mode)
                if (mode === 'all' && lastReqRef.current?.equals(req) && resultsCacheRef.current.length) {
                    return {
                        suggestions: null,
                        allSuggestions: buildArrayResponse(resultsCacheRef.current),
                        error: null,
                    }
                }

                // Make API request for all 4 variants
                const res = await notifSvc.generateNotificationVariants(domainId, req, {
                    showLoadingScreen: false,
                    cancellationKey: 'pushly-ai.generateNotificationVariants',
                })

                // Check for page analysis error
                const analysisError = res.data?.error ?? null
                if (analysisError) {
                    return { suggestions: null, allSuggestions: null, error: analysisError }
                }

                if (!res.ok || !res.data?.variants?.length) {
                    return { suggestions: buildResponse(null), allSuggestions: buildArrayResponse(null), error: null }
                }

                // Store the request_id from the API response
                generationIdRef.current = res.data?.request_id ?? null

                // Preload images once for each unique image URL
                const variantCompositeImageMap: Record<string, CompositeImage> = {}
                const uqVariantImageURLs = Array.from(
                    new Set(res.data.variants.filter((item) => !!item.image).map(({ image }) => image!)),
                )
                await Promise.all(
                    uqVariantImageURLs.map(async (imageUrl) => {
                        try {
                            const compositeImage = await loadCompositeImage(imageUrl)
                            if (compositeImage) {
                                variantCompositeImageMap[imageUrl] ??= compositeImage
                            }
                        } catch {
                            console.warn('[usePushlyAi] Failed to preload composite image', imageUrl)
                        }
                    }),
                )

                // Convert to INotificationFieldSuggestions with composite images
                const allVariants = res.data.variants.map<INotificationFieldSuggestions>((item) => {
                    let variantCompositeImage: CompositeImage | null = null
                    if (item.image && variantCompositeImageMap[item.image]) {
                        variantCompositeImage = variantCompositeImageMap[item.image]
                    }

                    return {
                        title: item.title,
                        body: item.body,
                        imageUrl: variantCompositeImage?.url ?? item.image,
                        compositeImage: variantCompositeImage,
                        keywords: sanitizeKeywords(item.keywords),
                        tone: item.tone,
                        reason: item.reason,
                    }
                })

                // Cache the results and request
                lastReqRef.current = req.clone()
                resultsCacheRef.current = allVariants.reverse()

                // Return based on mode
                if (mode === 'single') {
                    usedIndicesRef.current.clear()
                    usedIndicesRef.current.add(0)
                    lastServedVariantIndexRef.current = 0
                    return {
                        suggestions: buildResponse(resultsCacheRef.current[0]),
                        allSuggestions: null,
                        error: null,
                    }
                } else {
                    usedIndicesRef.current.clear()
                    return {
                        suggestions: null,
                        allSuggestions: buildArrayResponse(resultsCacheRef.current),
                        error: null,
                    }
                }
            } finally {
                setGenerating(false)
            }
        },
        [domainId, loadCompositeImage, notifSvc],
    )

    const generateNotificationVariant = useCallback(
        async (source: NotificationVariantModel): Promise<PushlyAiResult> => {
            return generateVariants(source, 'single')
        },
        [generateVariants],
    )

    const generateNotificationVariants = useCallback(
        async (source: NotificationVariantModel): Promise<PushlyAiResult> => {
            return generateVariants(source, 'all')
        },
        [generateVariants],
    )

    const markVariantsUsed = useCallback((indices: number[]) => {
        for (const idx of indices) {
            usedIndicesRef.current.add(idx)
        }
    }, [])

    return useMemo(
        () => ({
            generateNotificationVariant,
            generateNotificationVariants,
            markVariantsUsed,
            isGenerating,
            generationIdRef,
            lastServedVariantIndexRef,
        }),
        [generateNotificationVariant, generateNotificationVariants, markVariantsUsed, isGenerating],
    )
}
